// ============================================================================
// EDUSPHERE ENTERPRISE - COMPLETE ROUTE ARCHITECTURE
// Enterprise-grade routing configuration with data loaders and RBAC
// ============================================================================

import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { UserRole } from '@/types';

// ============================================================================
// ROUTE METADATA INTERFACE
// ============================================================================

export interface RouteMetadata {
  title: string;
  breadcrumb: string;
  description?: string;
  roles?: UserRole[];
  permissions?: string[];
  layout?: 'default' | 'minimal' | 'fullscreen' | 'print';
  showInNav?: boolean;
  navIcon?: string;
  navOrder?: number;
}

// ============================================================================
// COMPLETE PAGE TREE - ALL MODULE ROUTES
// ============================================================================

export const PAGE_TREE = {
  // =========================================================================
  // AUTHENTICATION
  // =========================================================================
  auth: {
    login: '/auth/login',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password/:token',
    mfaVerify: '/auth/mfa-verify',
    sessionExpired: '/auth/session-expired',
  },

  // =========================================================================
  // DASHBOARDS (RBAC-DRIVEN COMMAND CENTERS)
  // =========================================================================
  dashboard: {
    superAdmin: '/dashboard/super-admin',
    principal: '/dashboard/principal',
    academicHead: '/dashboard/academic-head',
    adminStaff: '/dashboard/admin-staff',
    accountant: '/dashboard/accountant',
    hrManager: '/dashboard/hr-manager',
    teacher: '/dashboard/teacher',
    classTeacher: '/dashboard/class-teacher',
    librarian: '/dashboard/librarian',
    transportManager: '/dashboard/transport-manager',
    storeKeeper: '/dashboard/store-keeper',
    parent: '/dashboard/parent',
    student: '/dashboard/student',
  },

  // =========================================================================
  // PRE-ADMISSION & CRM
  // =========================================================================
  admission: {
    // Lead Management
    leadList: '/admission/leads',
    leadCreate: '/admission/leads/create',
    leadProfile: '/admission/leads/:leadId',
    leadEdit: '/admission/leads/:leadId/edit',
    
    // Visit & Test Scheduling
    visitSchedule: '/admission/visits',
    visitCreate: '/admission/visits/create',
    visitCalendar: '/admission/visits/calendar',
    testSchedule: '/admission/tests',
    testCreate: '/admission/tests/create',
    testEvaluation: '/admission/tests/:testId/evaluate',
    
    // Online Admission
    applicationWizard: '/admission/apply',
    applicationList: '/admission/applications',
    applicationCreate: '/admission/applications/new',
    applicationReview: '/admission/applications/:applicationId',
    applicationApproval: '/admission/applications/:applicationId/approve',
    
    // Admission Processing
    admissionProcessing: '/admission/processing',
    admissionApprovalQueue: '/admission/approval-queue',
    admissionWorkflowSettings: '/admission/workflow-settings',
    admissionLeadPipelineSettings: '/admission/lead-pipeline-settings',
    bulkAdmission: '/admission/bulk-process',
    
    // Reports
    admissionFunnel: '/admission/reports/funnel',
    admissionAnalytics: '/admission/reports/analytics',
    sourceAnalysis: '/admission/reports/source-analysis',
    conversionReport: '/admission/reports/conversion',
  },

  // =========================================================================
  // STUDENT INFORMATION SYSTEM
  // =========================================================================
  students: {
    // Core Pages
    registry: '/students',
    create: '/students/create',
    profile: '/students/:studentId',
    edit: '/students/:studentId/edit',
    
    // Profile Tabs (Sub-routes)
    profileOverview: '/students/:studentId/overview',
    profileAcademic: '/students/:studentId/academic',
    profileAttendance: '/students/:studentId/attendance',
    profileExams: '/students/:studentId/exams',
    profileFees: '/students/:studentId/fees',
    profileTransport: '/students/:studentId/transport',
    profileLibrary: '/students/:studentId/library',
    profileDocuments: '/students/:studentId/documents',
    profileTimeline: '/students/:studentId/timeline',
    profileHealth: '/students/:studentId/health',
    
    // Bulk Operations
    bulkOperations: '/students/bulk-operations',
    bulkImport: '/students/bulk-import',
    bulkExport: '/students/bulk-export',
    bulkPromotion: '/students/bulk-promotion',
    bulkTransfer: '/students/bulk-transfer',
    
    // ID Card
    idCardGenerator: '/students/id-cards',
    idCardDesigner: '/students/id-cards/designer',
    idCardBulkPrint: '/students/id-cards/bulk-print',
    
    // Reports
    studentReports: '/students/reports',
    strengthReport: '/students/reports/strength',
    demographicsReport: '/students/reports/demographics',
    categoryReport: '/students/reports/category',
    ageAnalysis: '/students/reports/age-analysis',
    rteIntakeReport: '/students/reports/rte-intake',
    rteAnnexureIv: '/students/:studentId/rte-annexure-iv',
  },

  // =========================================================================
  // GUARDIAN / PARENT
  // =========================================================================
  guardians: {
    list: '/guardians',
    create: '/guardians/create',
    profile: '/guardians/:guardianId',
    edit: '/guardians/:guardianId/edit',
    linkStudent: '/guardians/:guardianId/link-student',
  },

  // =========================================================================
  // ACADEMIC ADMINISTRATION
  // =========================================================================
  academic: {
    // Class & Section Management
    classes: '/academic/classes',
    classCreate: '/academic/classes/create',
    classProfile: '/academic/classes/:classId',
    classEdit: '/academic/classes/:classId/edit',
    sections: '/academic/sections',
    sectionCreate: '/academic/sections/create',
    
    // Subject Management
    subjects: '/academic/subjects',
    subjectCreate: '/academic/subjects/create',
    subjectProfile: '/academic/subjects/:subjectId',
    subjectMapping: '/academic/subject-mapping',
    
    // Teacher Assignment
    teacherAssignment: '/academic/teacher-assignment',
    teacherAssignmentMatrix: '/academic/teacher-assignment/matrix',
    classTeacherAssignment: '/academic/class-teacher-assignment',
    
    // Timetable
    timetableStudio: '/academic/timetable',
    timetableCreate: '/academic/timetable/create',
    timetableEdit: '/academic/timetable/:timetableId/edit',
    timetableView: '/academic/timetable/:classId/:sectionId',
    timetableMaster: '/academic/timetable/master',
    substitutionManager: '/academic/timetable/substitution',
    teacherTimetable: '/academic/timetable/teacher/:teacherId',
    roomTimetable: '/academic/timetable/room/:roomId',
    
    // Academic Calendar
    calendar: '/academic/calendar',
    calendarEvents: '/academic/calendar/events',
    holidays: '/academic/holidays',
    
    // Reports
    academicReports: '/academic/reports',
    classStrength: '/academic/reports/class-strength',
    teacherWorkload: '/academic/reports/teacher-workload',
  },

  // =========================================================================
  // EXAMINATION & RESULTS
  // =========================================================================
  examination: {
    // Exam Definition
    examList: '/examination/exams',
    examCreate: '/examination/exams/create',
    examProfile: '/examination/exams/:examId',
    examEdit: '/examination/exams/:examId/edit',
    examSchedule: '/examination/exams/:examId/schedule',
    
    // Class-Exam Mapping
    classExamMapping: '/examination/class-mapping',
    classExamMappingCreate: '/examination/class-mapping/create',
    classExamMappingEdit: '/examination/class-mapping/:mappingId/edit',
    
    // Marks Entry
    marksEntry: '/examination/marks-entry',
    marksEntryGrid: '/examination/marks-entry/:examId/:classId/:subjectId',
    marksModeration: '/examination/marks-moderation',
    marksApproval: '/examination/marks-approval',
    marksLock: '/examination/marks-lock',
    
    // Grade Management
    gradeScales: '/examination/grade-scales',
    gradeScaleCreate: '/examination/grade-scales/create',
    gradeScaleEdit: '/examination/grade-scales/:scaleId/edit',
    
    // Report Cards
    reportCardDesigner: '/examination/report-card/designer',
    reportCardGenerate: '/examination/report-card/generate',
    reportCardPublish: '/examination/report-card/publish',
    reportCardBulkPrint: '/examination/report-card/bulk-print',
    
    // Results
    resultsPublishing: '/examination/results/publish',
    resultsAnalysis: '/examination/results/analysis',
    classResults: '/examination/results/class/:classId/:examId',
    studentResults: '/examination/results/student/:studentId',
    
    // Reports
    examReports: '/examination/reports',
    performanceAnalysis: '/examination/reports/performance',
    subjectAnalysis: '/examination/reports/subject-analysis',
    comparativeAnalysis: '/examination/reports/comparative',
    toppersList: '/examination/reports/toppers',
    failureAnalysis: '/examination/reports/failure-analysis',
  },

  // =========================================================================
  // ATTENDANCE MANAGEMENT
  // =========================================================================
  attendance: {
    // Student Attendance
    studentMarkingConsole: '/attendance/student/mark',
    studentMarkingClass: '/attendance/student/mark/:classId/:sectionId',
    studentBulkView: '/attendance/student/bulk-view',
    periodWiseAttendance: '/attendance/student/period-wise',
    
    // Staff Attendance
    staffAttendance: '/attendance/staff',
    staffBiometricSync: '/attendance/staff/biometric-sync',
    staffManualEntry: '/attendance/staff/manual-entry',
    
    // Leave Management
    leaveRequests: '/attendance/leave/requests',
    leaveRequestCreate: '/attendance/leave/create',
    leaveApproval: '/attendance/leave/approval',
    leaveTypes: '/attendance/leave/types',
    leaveBalance: '/attendance/leave/balance',
    
    // Reports
    attendanceReports: '/attendance/reports',
    dailyReport: '/attendance/reports/daily',
    monthlyReport: '/attendance/reports/monthly',
    classAttendanceReport: '/attendance/reports/class',
    studentAttendanceReport: '/attendance/reports/student/:studentId',
    staffAttendanceReport: '/attendance/reports/staff/:employeeId',
    absenteeReport: '/attendance/reports/absentee',
    attendanceIntelligence: '/attendance/reports/intelligence',
  },

  // =========================================================================
  // FINANCE & FEES
  // =========================================================================
  finance: {
    // Fee Structure
    feeStructures: '/finance/fee-structures',
    feeStructureCreate: '/finance/fee-structures/create',
    feeStructureEdit: '/finance/fee-structures/:structureId/edit',
    feeHeads: '/finance/fee-heads',
    feeHeadCreate: '/finance/fee-heads/create',
    
    // Student Ledger
    studentLedger: '/finance/student-ledger',
    studentLedgerView: '/finance/student-ledger/:studentId',
    
    // Fee Collection
    collectionPOS: '/finance/collection',
    collectionHistory: '/finance/collection/history',
    receiptArchive: '/finance/receipts',
    receiptView: '/finance/receipts/:receiptId',
    receiptReprint: '/finance/receipts/:receiptId/reprint',
    
    // Defaulters
    defaulterCommandCenter: '/finance/defaulters',
    defaulterList: '/finance/defaulters/list',
    defaulterNotifications: '/finance/defaulters/notifications',
    
    // Discounts & Waivers
    discountManagement: '/finance/discounts',
    discountCreate: '/finance/discounts/create',
    waiverApproval: '/finance/waivers/approval',
    
    // Refunds
    refundRequests: '/finance/refunds',
    refundProcess: '/finance/refunds/process',
    
    // Financial Reports
    financialDashboard: '/finance/dashboard',
    collectionReport: '/finance/reports/collection',
    dailyCollection: '/finance/reports/daily-collection',
    outstandingReport: '/finance/reports/outstanding',
    feeHeadWiseReport: '/finance/reports/fee-head-wise',
    classWiseCollection: '/finance/reports/class-wise',
    concessionReport: '/finance/reports/concession',
    refundReport: '/finance/reports/refunds',
  },

  // =========================================================================
  // HR & PAYROLL
  // =========================================================================
  hr: {
    // Employee Management
    employeeRegistry: '/hr/employees',
    employeeCreate: '/hr/employees/create',
    employeeProfile: '/hr/employees/:employeeId',
    employeeEdit: '/hr/employees/:employeeId/edit',
    
    // Employee Profile Tabs
    employeeOverview: '/hr/employees/:employeeId/overview',
    employeeDocuments: '/hr/employees/:employeeId/documents',
    employeeSalary: '/hr/employees/:employeeId/salary',
    employeeAttendance: '/hr/employees/:employeeId/attendance',
    employeeLeave: '/hr/employees/:employeeId/leave',
    employeePayslips: '/hr/employees/:employeeId/payslips',
    
    // Departments & Designations
    departments: '/hr/departments',
    departmentCreate: '/hr/departments/create',
    designations: '/hr/designations',
    designationCreate: '/hr/designations/create',
    
    // Salary Structure
    salaryStructures: '/hr/salary-structures',
    salaryStructureCreate: '/hr/salary-structures/create',
    salaryStructureEdit: '/hr/salary-structures/:structureId/edit',
    salaryComponents: '/hr/salary-components',
    payrollConfig: '/hr/payroll-config',

    // Payroll Processing
    payrollConsole: '/hr/payroll',
    payrollProcess: '/hr/payroll/process',
    payrollPreview: '/hr/payroll/preview/:runId',
    payrollApproval: '/hr/payroll/approval',
    payrollHistory: '/hr/payroll/history',
    
    // Payslips
    payslipCenter: '/hr/payslips',
    payslipGenerate: '/hr/payslips/generate',
    payslipBulkPublish: '/hr/payslips/bulk-publish',
    payslipView: '/hr/payslips/:payslipId',
    
    // HR Reports
    hrDashboard: '/hr/dashboard',
    employeeReport: '/hr/reports/employees',
    attendancePayrollBridge: '/hr/reports/attendance-payroll',
    salaryRegister: '/hr/reports/salary-register',
    departmentWiseReport: '/hr/reports/department-wise',
    headcountAnalysis: '/hr/reports/headcount',
  },

  // =========================================================================
  // INVENTORY & ASSETS
  // =========================================================================
  inventory: {
    // Inventory
    inventoryRegister: '/inventory/items',
    inventoryCreate: '/inventory/items/create',
    inventoryProfile: '/inventory/items/:itemId',
    inventoryEdit: '/inventory/items/:itemId/edit',
    inventoryCategories: '/inventory/categories',
    
    // Stock Movement
    stockMovementLedger: '/inventory/stock-movements',
    stockIn: '/inventory/stock-in',
    stockOut: '/inventory/stock-out',
    stockAdjustment: '/inventory/stock-adjustment',
    stockTransfer: '/inventory/stock-transfer',
    
    // Purchase
    purchaseOrders: '/inventory/purchase-orders',
    purchaseOrderCreate: '/inventory/purchase-orders/create',
    purchaseOrderView: '/inventory/purchase-orders/:orderId',
    suppliers: '/inventory/suppliers',
    supplierCreate: '/inventory/suppliers/create',
    
    // Assets
    assetMaster: '/inventory/assets',
    assetCreate: '/inventory/assets/create',
    assetProfile: '/inventory/assets/:assetId',
    assetEdit: '/inventory/assets/:assetId/edit',
    assetAllocation: '/inventory/assets/allocation',
    assetMaintenance: '/inventory/assets/maintenance',
    assetAudit: '/inventory/assets/audit',
    
    // Reports
    inventoryReports: '/inventory/reports',
    stockReport: '/inventory/reports/stock',
    movementReport: '/inventory/reports/movement',
    lowStockAlert: '/inventory/reports/low-stock',
    assetRegister: '/inventory/reports/asset-register',
    depreciationReport: '/inventory/reports/depreciation',
    auditTrail: '/inventory/reports/audit-trail',
  },

  // =========================================================================
  // TRANSPORT MANAGEMENT
  // =========================================================================
  transport: {
    // Routes
    routeDesigner: '/transport/routes',
    routeCreate: '/transport/routes/create',
    routeProfile: '/transport/routes/:routeId',
    routeEdit: '/transport/routes/:routeId/edit',
    routeStops: '/transport/routes/:routeId/stops',
    
    // Vehicles
    vehicleFleet: '/transport/vehicles',
    vehicleCreate: '/transport/vehicles/create',
    vehicleProfile: '/transport/vehicles/:vehicleId',
    vehicleEdit: '/transport/vehicles/:vehicleId/edit',
    vehicleMaintenance: '/transport/vehicles/:vehicleId/maintenance',
    
    // Student Assignment
    studentAssignment: '/transport/student-assignment',
    studentAssignmentBulk: '/transport/student-assignment/bulk',
    
    // Transport Billing
    transportBilling: '/transport/billing',
    transportBillingLink: '/transport/billing/link-fees',
    
    // GPS Tracking
    liveTracking: '/transport/tracking',
    routePlayback: '/transport/tracking/playback/:routeId',
    
    // Reports
    transportReports: '/transport/reports',
    routeWiseReport: '/transport/reports/route-wise',
    vehicleUtilization: '/transport/reports/utilization',
    fuelReport: '/transport/reports/fuel',
    maintenanceReport: '/transport/reports/maintenance',
  },

  // =========================================================================
  // LIBRARY MANAGEMENT
  // =========================================================================
  library: {
    // Book Catalog
    bookCatalog: '/library/books',
    bookCreate: '/library/books/create',
    bookProfile: '/library/books/:bookId',
    bookEdit: '/library/books/:bookId/edit',
    bookCategories: '/library/categories',
    
    // Transactions
    issueReturn: '/library/transactions',
    issueBook: '/library/transactions/issue',
    returnBook: '/library/transactions/return',
    renewBook: '/library/transactions/renew',
    reserveBook: '/library/transactions/reserve',
    
    // Fines
    fineConsole: '/library/fines',
    fineCollection: '/library/fines/collect',
    fineWaiver: '/library/fines/waiver',
    
    // Member History
    memberHistory: '/library/history/:memberId',
    studentReadingHistory: '/library/history/student/:studentId',
    staffReadingHistory: '/library/history/staff/:employeeId',
    
    // Reports
    libraryReports: '/library/reports',
    circulationReport: '/library/reports/circulation',
    overdueReport: '/library/reports/overdue',
    popularBooks: '/library/reports/popular',
    memberActivity: '/library/reports/member-activity',
    inventoryReport: '/library/reports/inventory',
  },

  // =========================================================================
  // COMMUNICATION
  // =========================================================================
  communication: {
    // Announcements
    announcements: '/communication/announcements',
    announcementCreate: '/communication/announcements/create',
    announcementView: '/communication/announcements/:announcementId',
    
    // Messaging
    messages: '/communication/messages',
    messageCompose: '/communication/messages/compose',
    messageThread: '/communication/messages/:threadId',
    
    // Notifications
    notificationCenter: '/communication/notifications',
    notificationSettings: '/communication/notifications/settings',
    
    // SMS & Email
    smsBroadcast: '/communication/sms',
    emailBroadcast: '/communication/email',
    communicationHistory: '/communication/history',
    
    // Templates
    messageTemplates: '/communication/templates',
    templateCreate: '/communication/templates/create',
  },

  // =========================================================================
  // SETTINGS & CONFIGURATION
  // =========================================================================
  settings: {
    // General
    generalSettings: '/settings/general',
    branchSettings: '/settings/branches',
    academicYearSettings: '/settings/academic-years',
    
    // User Management
    users: '/settings/users',
    userCreate: '/settings/users/create',
    userProfile: '/settings/users/:userId',
    userEdit: '/settings/users/:userId/edit',
    roles: '/settings/roles',
    roleCreate: '/settings/roles/create',
    roleEdit: '/settings/roles/:roleId/edit',
    permissions: '/settings/permissions',
    
    // System Configuration
    feeConfiguration: '/settings/fee-config',
    examConfiguration: '/settings/exam-config',
    attendanceConfiguration: '/settings/attendance-config',
    gradeConfiguration: '/settings/grade-config',
    
    // Masters
    rooms: '/settings/rooms',
    roomCreate: '/settings/rooms/create',
    
    // Integrations
    integrations: '/settings/integrations',
    paymentGateway: '/settings/integrations/payment',
    smsGateway: '/settings/integrations/sms',
    emailGateway: '/settings/integrations/email',
    biometricIntegration: '/settings/integrations/biometric',

    // Notification Templates (Wave 6)
    notificationTemplates: '/settings/notification-templates',

    // Audit & Logs
    auditLogs: '/settings/audit-logs',
    systemLogs: '/settings/system-logs',
    loginHistory: '/settings/login-history',
  },

  // =========================================================================
  // REPORTS HUB
  // =========================================================================
  reports: {
    hub: '/reports',
    builder: '/reports/builder',
    scheduled: '/reports/scheduled',
    favorites: '/reports/favorites',
    exports: '/reports/exports',
  },
} as const;

// ============================================================================
// ROUTE METADATA DEFINITIONS
// ============================================================================

export const ROUTE_METADATA: Record<string, RouteMetadata> = {
  // Dashboard Metadata
  [PAGE_TREE.dashboard.superAdmin]: {
    title: 'Super Admin Dashboard',
    breadcrumb: 'Dashboard',
    roles: ['super_admin'],
    layout: 'default',
    showInNav: true,
    navIcon: 'Dashboard',
    navOrder: 1,
  },
  [PAGE_TREE.dashboard.principal]: {
    title: 'Principal Dashboard',
    breadcrumb: 'Dashboard',
    roles: ['principal', 'vice_principal'],
    layout: 'default',
    showInNav: true,
    navIcon: 'Dashboard',
    navOrder: 1,
  },
  [PAGE_TREE.dashboard.teacher]: {
    title: 'Teacher Dashboard',
    breadcrumb: 'Dashboard',
    roles: ['teacher', 'class_teacher'],
    layout: 'default',
    showInNav: true,
    navIcon: 'Dashboard',
    navOrder: 1,
  },
  [PAGE_TREE.dashboard.parent]: {
    title: 'Parent Portal',
    breadcrumb: 'Dashboard',
    roles: ['parent'],
    layout: 'default',
    showInNav: true,
    navIcon: 'Dashboard',
    navOrder: 1,
  },

  // Student Pages Metadata
  [PAGE_TREE.students.registry]: {
    title: 'Student Registry',
    breadcrumb: 'Students',
    roles: ['super_admin', 'principal', 'vice_principal', 'academic_head', 'admin_staff', 'teacher', 'class_teacher'],
    permissions: ['students:read'],
    layout: 'default',
    showInNav: true,
    navIcon: 'People',
    navOrder: 2,
  },
  [PAGE_TREE.students.create]: {
    title: 'New Student Admission',
    breadcrumb: 'New Student',
    roles: ['super_admin', 'principal', 'admin_staff'],
    permissions: ['students:create'],
    layout: 'default',
  },
  [PAGE_TREE.students.profile]: {
    title: 'Student Profile',
    breadcrumb: 'Profile',
    roles: ['super_admin', 'principal', 'vice_principal', 'academic_head', 'admin_staff', 'teacher', 'class_teacher', 'accountant'],
    permissions: ['students:read'],
    layout: 'default',
  },

  // Finance Pages Metadata
  [PAGE_TREE.finance.collectionPOS]: {
    title: 'Fee Collection',
    breadcrumb: 'Collection',
    // Widened: principal can access the Finance module per NAVIGATION_CONFIG, so
    // must not be locked out of the collection screen.
    roles: ['super_admin', 'principal', 'accountant', 'admin_staff'],
    permissions: ['finance:collect'],
    layout: 'minimal',
    showInNav: true,
    navIcon: 'Payment',
    navOrder: 6,
  },
  [PAGE_TREE.finance.defaulterCommandCenter]: {
    title: 'Defaulter Command Center',
    breadcrumb: 'Defaulters',
    // Widened: admin_staff is part of the Finance module audience in NAVIGATION_CONFIG.
    roles: ['super_admin', 'accountant', 'principal', 'admin_staff'],
    permissions: ['finance:read'],
    layout: 'default',
  },

  // Examination Pages Metadata
  [PAGE_TREE.examination.marksEntry]: {
    title: 'Marks Entry',
    breadcrumb: 'Marks Entry',
    // Widened: principal can access the Examination module per NAVIGATION_CONFIG.
    roles: ['super_admin', 'principal', 'academic_head', 'teacher', 'class_teacher'],
    permissions: ['examination:marks_entry'],
    layout: 'fullscreen',
  },

  // HR Pages Metadata
  [PAGE_TREE.hr.payrollConsole]: {
    title: 'Payroll Processing',
    breadcrumb: 'Payroll',
    roles: ['super_admin', 'hr_manager', 'accountant'],
    permissions: ['payroll:process'],
    layout: 'default',
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getRouteWithParams(route: string, params: Record<string, string>): string {
  let result = route;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, value);
  });
  return result;
}

export function matchRoute(path: string, pattern: string): Record<string, string> | null {
  const pathParts = path.split('/');
  const patternParts = pattern.split('/');
  
  if (pathParts.length !== patternParts.length) return null;
  
  const params: Record<string, string> = {};
  
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) {
      params[patternParts[i].slice(1)] = pathParts[i];
    } else if (patternParts[i] !== pathParts[i]) {
      return null;
    }
  }
  
  return params;
}

export function isRouteAllowed(route: string, userRoles: UserRole[]): boolean {
  const metadata = ROUTE_METADATA[route];
  if (!metadata?.roles) return true;
  return userRoles.some(role => metadata.roles?.includes(role));
}

/**
 * Roles that bypass ALL route-level RBAC gating. Kept as a plain string[] (not
 * UserRole[]) because legacy/seeded accounts may carry an 'admin' role that is
 * not part of the typed UserRole union but must still have full access.
 */
export const RBAC_BYPASS_ROLES: readonly string[] = ['super_admin', 'admin'];

/**
 * Module-level access policy — keyed by the FIRST path segment of the
 * pathname. This is the DEFAULT-DENY backbone of the route guard: every
 * top-level route family in router.tsx must appear here (or carry exact
 * ROUTE_METADATA roles); an unlisted segment is blocked for everyone except
 * the bypass roles, so a newly added module is closed until someone decides
 * who may see it.
 *
 * Role lists mirror the NAVIGATION_CONFIG module audiences and the backend
 * rbac() lists for the same module (hostel/midday-meal are not in the nav, so
 * their lists come from hostel.routes.ts / attendance.routes.ts). Typed as
 * string[] because some backend roles (e.g. hostel_warden) are not part of
 * the UI's UserRole union. super_admin/admin are omitted — the bypass covers
 * them. 'any' = any authenticated user.
 */
export const MODULE_ROUTE_ACCESS: Record<string, readonly string[] | 'any'> = {
  modules: 'any',
  dashboard: 'any',
  admission: ['principal', 'vice_principal', 'admin_staff'],
  // Legacy alias of /admission (consolidated lead workspace).
  crm: ['principal', 'vice_principal', 'admin_staff'],
  students: ['principal', 'vice_principal', 'academic_head', 'admin_staff', 'teacher', 'class_teacher', 'accountant'],
  academic: ['principal', 'vice_principal', 'academic_head', 'teacher', 'class_teacher'],
  attendance: ['principal', 'academic_head', 'admin_staff', 'teacher', 'class_teacher', 'hr_manager'],
  'midday-meal': ['principal', 'academic_head', 'admin_staff', 'teacher', 'class_teacher'],
  examination: ['principal', 'academic_head', 'teacher', 'class_teacher'],
  finance: ['principal', 'accountant', 'admin_staff'],
  hr: ['principal', 'hr_manager', 'accountant'],
  transport: ['transport_manager', 'admin_staff'],
  library: ['librarian', 'teacher', 'class_teacher'],
  hostel: ['principal', 'admin_staff', 'hostel_warden'],
  communication: ['principal', 'vice_principal', 'admin_staff', 'teacher', 'class_teacher'],
  settings: ['principal'],
  templates: ['principal', 'accountant', 'hr_manager', 'academic_head'],
  reports: ['principal', 'vice_principal', 'academic_head', 'accountant', 'hr_manager'],
  inventory: ['store_keeper', 'admin_staff', 'accountant'],
};

/**
 * Runtime route-guard decision used by the single ProtectedRoute chokepoint.
 *
 * DEFAULT-DENY policy:
 *  - super_admin / admin always pass (bypass).
 *  - An exact ROUTE_METADATA entry with a roles list is most specific and
 *    decides on its own.
 *  - Otherwise the first path segment is looked up in MODULE_ROUTE_ACCESS
 *    (dynamic/detail routes like /students/:id inherit their module's
 *    audience). 'any' admits every authenticated user.
 *  - A segment in neither map is DENIED — new routes are closed by default
 *    and must be granted an audience here or in ROUTE_METADATA.
 *
 * @param pathname current location pathname (no query/hash)
 * @param userRoles live roles from the auth store
 */
export function canAccessRoute(pathname: string, userRoles: UserRole[]): boolean {
  // Admin/super-admin bypass everything.
  if (userRoles.some(role => RBAC_BYPASS_ROLES.includes(role))) return true;

  // Most specific first: an exact metadata entry with roles decides.
  const metadata = ROUTE_METADATA[pathname];
  if (metadata?.roles) return isRouteAllowed(pathname, userRoles);

  // Root ('/') is only the index redirect to /modules — always allowed.
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment) return true;

  const access = MODULE_ROUTE_ACCESS[segment];
  if (access === 'any') return true;
  if (!access) return false; // default-deny: unknown route family
  return userRoles.some((role) => access.includes(role));
}
